import { useEffect, useState } from "react";
import { db } from "../services/firebase";
import { ref, onValue, get, child, off } from "firebase/database";

/**
 * ✅ Fetches questions for an interview in the exact order
 * defined under InterviewQuestions node (admin-defined).
 * Ensures orderId from InterviewQuestions is preserved (not from Questions).
 */
export default function useFetchQuestions(domainId, packId, interviewId) {
  const [questions, setQuestions] = useState([]);
  const [loadingQuestions, setLoading] = useState(true);

  useEffect(() => {
    if (!domainId || !packId || !interviewId) return;

    const interviewRef = ref(db, `InterviewQuestions/${domainId}/${packId}/${interviewId}`);

    const unsubscribe = onValue(interviewRef, async (snapshot) => {
      const iqData = snapshot.val();
      if (!iqData) {
        setQuestions([]);
        setLoading(false);
        return;
      }

      // 1️⃣ Extract question IDs and orderIds from InterviewQuestions
      const ordered = Object.entries(iqData)
        .map(([qid, info]) => ({ id: qid, orderId: info?.orderId ?? 0 }))
        .sort((a, b) => (a.orderId ?? 0) - (b.orderId ?? 0));

      // 2️⃣ Fetch full question details
      const fetches = ordered.map(async ({ id, orderId }) => {
        const qSnap = await get(child(ref(db), `Questions/AllQuestions/${domainId}/${id}`));
        if (!qSnap.exists()) return null;
        const qData = qSnap.val();

        // ✅ Preserve InterviewQuestions.orderId, not overwritten by Questions data
        return { id, ...qData, orderId };
      });

      const fullQuestions = (await Promise.all(fetches)).filter(Boolean);

      // 3️⃣ Strictly keep order as InterviewQuestions.orderId
      fullQuestions.sort((a, b) => (a.orderId ?? 0) - (b.orderId ?? 0));

      setQuestions(fullQuestions);
      setLoading(false);
    });

    return () => {
      off(interviewRef);
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, [domainId, packId, interviewId]);

  return { questions, loadingQuestions };
}
